/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { CustomError } from '@agilelab/plugin-wb-platform-common';
import {
  identityApiRef,
  ProfileInfo,
  useApi,
} from '@backstage/core-plugin-api';
import { useEffect } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { customAlertApiRef } from '../apis';
import { useEnrichedIdentity } from './useEnrichedIdentity';

/** @public */
export const useUserProfile = () => {
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const {
    identity: enrichedIdentity,
    loading: identityLoading,
    error: identityError,
  } = useEnrichedIdentity();

  const { value, loading, error } = useAsync(async () => {
    if (!enrichedIdentity) {
      return undefined;
    }
    const profile = await identityApi.getProfileInfo();
    return { profile, identity: enrichedIdentity };
  }, [enrichedIdentity]);

  useEffect(() => {
    if (error || identityError) {
      alertApi.post({
        error: new CustomError(
          'Failed to load user identity',
          (error || identityError)?.message || 'Unknown error',
        ),
        severity: 'error',
      });
    }
  }, [error, identityError, alertApi]);

  if (loading || identityLoading || error || identityError || !value) {
    return {
      profile: {} as ProfileInfo,
      displayName: '',
      loading: loading || identityLoading,
    };
  }

  return {
    profile: value!.profile,
    backstageIdentity: value!.identity,
    displayName: value!.profile.displayName ?? value!.identity.userEntityRef,
    loading,
  };
};
